import React, {
  ComponentProps,
  RefObject,
  useCallback,
  useRef,
  useState,
} from 'react';
import { Modal, TouchableOpacity } from 'react-native';

import { DateRange } from '@components/DateRangePicker/DateRangePickerModal';
import { Box, Text } from '@components/Restyle';
import Icon from '@components/shared/Icon/Icon';
import { PopupTaskFilterProjectDateRangeV3 } from '@components/Web/PopupTaskFilterProjectV3.web';
import { useOutsideClick } from '@hooks/useOutsideClick.web';
import documentIcons from '@themes/documentIcons';
import Images from '@themes/images';
import { getScaledSize, SCALED_DIMENSIONS } from '@themes/theme';

type PositionTuple = ['left' | 'right', 'top' | 'bottom'];

type Position = PositionTuple | 'top' | 'bottom' | 'left' | 'right';

type Props = Omit<ComponentProps<typeof TouchableOpacity>, 'onPress'> & {
  title: string;
  iconName: keyof typeof Images | keyof typeof documentIcons;
  onDismiss?: any;

  onSelectDuedate?: (dr: DateRange) => void;
  selectedDuedate?: DateRange;
  disabled?: boolean;
  boxProps: Partial<{
    position: Position;
    offset: [number, number] | number;
  }>;
};

export const MyTasksDueDateRangePicker = ({
  onDismiss,
  boxProps: { position, offset },
  title: xtitle,
  iconName,
  selectedDuedate,
  onSelectDuedate,
  disabled = false,
  accessibilityLabel,
}: Props) => {
  const [visible, setVisible] = useState(false);

  // Toggle popup visibility
  const togglePopup = () => setVisible((v) => !v);

  // Helper to check if mouse event is inside a given DOM rect
  const isEventPointInArea = (
    ref: RefObject<HTMLDivElement>,
    event: MouseEvent
  ) => {
    const rect = ref?.current?.getBoundingClientRect();
    return (
      rect &&
      event.x > rect.x &&
      event.x < rect.x + rect.width &&
      event.y > rect.y &&
      event.y < rect.y + rect.height
    );
  };

  // Ref for the popup container inside the Modal
  const ref2 = useRef<HTMLDivElement>();

  // Optional ref for a close icon (if you render one later)
  const iconCloseRef = useRef<HTMLDivElement>();

  const isEventPointInModal = (event: MouseEvent) => {
    return isEventPointInArea(ref2, event);
  };

  const isEventPointInCloseIcon = (event: MouseEvent) => {
    return isEventPointInArea(iconCloseRef, event);
  };

  // Clear selected date range
  const clearState = () => {
    onSelectDuedate?.({});
  };

  // Handle click outside of the popup
  const handleOutsideClick = useCallback((event: MouseEvent) => {
    if (!isEventPointInModal(event)) {
      if (isEventPointInCloseIcon(event)) {
        // If click is on close icon area, clear the state
        clearState();
      } else {
        // Otherwise just close the popup
        setVisible(false);
      }
    }
  }, []);

  // Ref for the outer container, used only for outside click detection
  const containerRef = useOutsideClick<HTMLDivElement>(handleOutsideClick);

  // Ref for the trigger button area, used only for position calculation
  const anchorRef = useRef<HTMLDivElement | null>(null);

  // Store the natural dimensions of the popup container
  const [layout, setLayout] = useState<
    { width: number; height: number } | undefined
  >();

  const [mouseOver, setMouseOver] = useState(false);

  // Compute popup position based on trigger (anchor) rect and popup size
  const adjustBounds = (
    position: Props['boxProps']['position'] = 'bottom',
    offset: Props['boxProps']['offset'] = 0,
    client: DOMRect,
    popupLayout: { width: number; height: number }
  ) => {
    const [offsetLeftRight, offsetTopBottom] =
      typeof offset === 'number' ? [offset, offset] : offset;

    // Normalize position to a tuple [leftRight, topBottom]
    const normalizedPosition = (typeof position !== 'string' && position) ||
      ((position === 'left' || position === 'right') && [position, 'bottom']) ||
      ((position === 'top' || position === 'bottom') && ['left', position]) || [
        'left',
        'bottom',
      ];

    const [leftRight, topBottom] = normalizedPosition;

    const bounds = {
      // Vertical placement: above or below the anchor
      top:
        topBottom === 'top'
          ? client.top - popupLayout.height - offsetTopBottom
          : client.top + client.height + offsetTopBottom,
      // Horizontal placement:
      // 'left' branch means align popup's right edge with anchor's right edge (right-aligned)
      // 'right' branch means align popup's left edge with anchor's left edge (left-aligned)
      left:
        leftRight === 'left'
          ? client.left + client.width - popupLayout.width - offsetLeftRight
          : client.left + offsetLeftRight,
    };

    return bounds;
  };

  // Use anchor element's rect to compute popup bounds
  const anchorRect = anchorRef.current?.getBoundingClientRect();
  const adjustedBounds =
    anchorRect && layout && adjustBounds(position, offset, anchorRect, layout);

  const onModalDismiss = () => {
    onDismiss?.();
    setVisible(false);
  };

  const backgroundColor1 = () => {
    if (mouseOver || visible) return 'grey01';
    return 'white';
  };

  return (
    <Box accessibilityLabel={accessibilityLabel} ref={containerRef}>
      {/* Anchor wrapper: only this area is used for positioning the popup */}
      <Box ref={anchorRef}>
        <TouchableOpacity
          disabled={disabled}
          accessibilityLabel={xtitle}
          onPress={togglePopup}>
          <Box
            height={getScaledSize(SCALED_DIMENSIONS.BUTTON_HEIGHT)}
            flexDirection='row'
            py='xs'
            px='m'
            maxWidth={200}
            onMouseEnter={() => {
              !disabled && setMouseOver(true);
            }}
            onMouseLeave={() => {
              !disabled && setMouseOver(false);
            }}
            backgroundColor={backgroundColor1()}
            borderWidth={1}
            borderColor='grey02'
            borderRadius='m'
            alignItems='center'
            justifyContent='center'>
            <Icon variant='s' name={iconName} color='grey05' />
            <Text
              marginLeft='xs'
              numberOfLines={1}
              variant='webBodySecondary'
              color='textSecondary'>
              {xtitle}
            </Text>
          </Box>
        </TouchableOpacity>
      </Box>

      {/* Hidden popup for measuring layout size (width/height).
          It is rendered off-screen and only used to get layout dimensions once. */}
      {!layout && (
        <Box
          left='-200vw'
          position='absolute'
          visible={!layout}
          onLayout={({ nativeEvent: { layout } }) => {
            // Only set layout once, to avoid re-measuring
            setLayout((curr) => curr ?? layout);
          }}>
          <PopupTaskFilterProjectDateRangeV3
            selectedDueDateRange={selectedDuedate}
            onSelectDueDateRange={(dr) => onSelectDuedate(dr)}
            isRangeSelector={false}
          />
        </Box>
      )}

      {/* Real popup rendered in a Modal, using the measured layout and anchor rect */}
      <Modal
        visible={visible}
        transparent
        onDismiss={onModalDismiss}
        onRequestClose={onModalDismiss}>
        <Box ref={ref2} style={{ ...adjustedBounds }} width={layout?.width}>
          <Box flexShrink={1}>
            <PopupTaskFilterProjectDateRangeV3
              selectedDueDateRange={selectedDuedate}
              onSelectDueDateRange={(dr) => {
                onSelectDuedate(dr);
                onModalDismiss();
              }}
              isRangeSelector={true}
            />
          </Box>
        </Box>
      </Modal>
    </Box>
  );
};
